import { Field, Modal, TextField, ActionButton, Button, Autocomplete } from 'components/commons';
import { useApi, useForm, useModal } from 'hooks';
import React, { useMemo } from 'react';
import { initialState as formState } from './product-form.state';
import styles from './product.module.scss';
import { NotifModal } from 'components/modals';
import locale from 'localization';
import { useEffect } from 'react';
import { handleRequest } from 'utils';
import { createProduct, updateProduct } from 'apis/product.api';
import { Grid } from '@material-ui/core';
import { InputAmount } from 'components/field';
import moment from 'moment';
import { generateCustomerClaim } from 'apis/customer.api';

const ProductForm = ({ initialState, actionText, create, productId, refreshPage, type, ...state }) => {
  const { close } = state;
  const notifModal = useModal();
  const form = useMemo(() => {
    return formState(initialState);
  }, [initialState]);

  const { fields, modifyField, submitForm, getFormValues, validateField, clearForm, isFormSubmittable } = useForm({
    initialState: form
  });

  useEffect(() => {
    if (state.active) {
      clearForm();
    }
    //eslint-disable-next-line
  }, [state.active]);

  const addProductRequest = useApi({
    api: createProduct,
    handleOwnError: {
      badrequest: true
    }
  });

  const editProductRequest = useApi({
    api: updateProduct,
    handleOwnError: {
      badrequest: true
    },
    params: {
      productId
    }
  });

  const generateCustomerClaimRequest = useApi({
    api: generateCustomerClaim,
    handleOwnError: {
      badrequest: true
    },
    params: {
      productId
    }
  });

  const generateCustomerClaimCb = () => {
    const params = getFormValues();

    handleRequest(async () => {
      const finalParams = {
        periodYear: params.year ? Number(params.year) : null,
        terms: params?.terms ? Number(params.terms) : null,
        interest: Number(params?.interest),
        productId,
        userId: Number(localStorage.getItem('userId'))
      };
      await generateCustomerClaimRequest.request(finalParams, () => submit());

      close();
      refreshPage();
      notifModal.show({
        title: 'Generate Success',
        contentHighlight: [<b>{fields?.name.value}</b>, <b>{params.year}</b>],
        content: `The {0} for the year {1} has been successfully generated. Please wait 15 minutes for the generational computation to complete.`,
        secondary: null
      });
    });
  };

  const loading = addProductRequest.loading || editProductRequest.loading || generateCustomerClaimRequest.loading;
  // const lastYear = moment().subtract(1, 'year').year();
  const lastYear = moment().year();

  const submit = async () => {
    const params = getFormValues();

    const apiRequest = create ? addProductRequest : editProductRequest;

    if (params.interest) {
      params.interest = Number(params.interest);
    } else {
      delete params.interest;
    }

    if (params.terms) {
      params.terms = Number(params.terms);
    } else {
      delete params.terms;
    }

    if (params.year) {
      params.year = Number(params.year);
    } else {
      delete params.year;
    }

    handleRequest(async () => {
      const finalParams = {
        ...params
      };
      await apiRequest.request(finalParams, () => submit());

      close();
      refreshPage();
      notifModal.show({
        title: create ? locale.userSuccessRegistered : locale.productSuccessEdited,
        contentHighlight: [<b>{params.name}</b>],
        content: create ? locale.successfullyRegisteredUser : locale.successfullyChangeInfoOfUser,
        secondary: create
          ? {
              text: 'Create New Product',
              onClick: () => {
                state.show({
                  title: 'Add Product',
                  actionText: locale.add,
                  create: true
                });
              }
            }
          : null
      });
    });
  };

  return (
    <div>
      <NotifModal {...notifModal} />
      <Modal close={loading ? false : close} {...state} className={styles.modalContent}>
        <div className={styles.formContent}>
          <div className={styles.content}>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} sm={4}>
                <Field {...fields.type}>
                  <Autocomplete
                    name="type"
                    value={fields.type}
                    options={[
                      {
                        label: 'shared-capital',
                        value: 'shared-capital'
                      },
                      // {
                      //   label: 'fixed-deposit',
                      //   value: 'fixed-deposit'
                      // },
                      {
                        label: 'savings',
                        value: 'savings'
                      }
                    ]}
                    {...fields.type}
                    onChange={modifyField}
                  />
                </Field>
              </Grid>
            </Grid>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} sm={4}>
                <Field {...fields.code}>
                  <TextField {...fields.code} onChange={modifyField} onBlur={validateField} />
                </Field>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Field {...fields.name}>
                  <TextField {...fields.name} onChange={modifyField} onBlur={validateField} />
                </Field>
              </Grid>
            </Grid>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} sm={4}>
                <Field {...fields.interest}>
                  <InputAmount {...fields.interest} onChange={modifyField} />
                </Field>
              </Grid>
              {!create && ['shared-capital'].includes(fields.type.value) && (
                <>
                  <Grid item xs={12} sm={4}>
                    <Field {...fields.year}>
                      <Autocomplete
                        name="type"
                        value={fields.year}
                        options={[
                          {
                            label: `${lastYear}`,
                            value: `${lastYear}`
                          }
                        ]}
                        {...fields.year}
                        onChange={modifyField}
                      />
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Field>
                      <Button
                        primary
                        loading={loading}
                        className={styles.generateButton}
                        onClick={generateCustomerClaimCb}
                        disabled={!fields.year.value || !fields.interest.value}
                      >
                        Generate
                      </Button>
                    </Field>
                  </Grid>
                </>
              )}
              {['fixed-deposit'].includes(fields.type.value) && (
                <Grid item xs={12} sm={4}>
                  <Field {...fields.terms} label={'Terms (Days)'}>
                    <TextField {...fields.terms} onChange={modifyField} />
                  </Field>
                </Grid>
              )}
            </Grid>
          </div>
        </div>
        <ActionButton
          right
          items={[
            {
              onClick: () => {
                state.close();
              },
              disabled: loading,
              text: locale.cancel
            },
            {
              onClick: () => {
                submitForm(submit);
              },
              disabled: !isFormSubmittable || (fields.name.value.includes('Fixed Deposit') ? !fields.terms.value : false),
              loading,
              text: actionText
            }
          ]}
        />
      </Modal>
    </div>
  );
};

export default ProductForm;
